import { Node, mergeAttributes } from "@tiptap/core";
import { ReactNodeViewRenderer, NodeViewWrapper, NodeViewProps } from "@tiptap/react";
import { useIsMobile } from "@/hooks/use-mobile";
import { optimizeImage, optimizeImageSrcSet } from "@/lib/imageOptimization";
import { useSmartImage } from '@/hooks/useSmartImage';

/**
 * Layout-aware width configuration for Cloudflare Image Optimization.
 */
interface LayoutConfig {
  mainWidth: number;
  srcSetWidths: number[];
  sizes: string;
}

const LAYOUT_CONFIGS: Record<string, LayoutConfig> = {
  inline: {
    mainWidth: 800,
    srcSetWidths: [400, 600, 800],
    sizes: "(max-width: 768px) 100vw, 700px",
  },
  default: {
    mainWidth: 1200,
    srcSetWidths: [600, 900, 1200],
    sizes: "(max-width: 768px) 100vw, (max-width: 1200px) 80vw, 1200px",
  },
  fullWidth: {
    mainWidth: 1920,
    srcSetWidths: [600, 900, 1200, 1920],
    sizes: "100vw",
  },
};

/**
 * Determine layout config based on the layout class string.
 */
function getLayoutConfig(layoutClass: string): LayoutConfig {
  if (layoutClass.includes("media-inline")) return LAYOUT_CONFIGS.inline;
  if (layoutClass.includes("media-full-width")) return LAYOUT_CONFIGS.fullWidth;
  return LAYOUT_CONFIGS.default;
}

/**
 * Translate desktop media layout to mobile layout:
 * - Breakout (media-default) → Full width (media-full-width)
 * - Breakout cropped (media-default-cropped) → Banner (media-full-width-cropped)
 * - Full width, Banner, Inline → unchanged
 */
function getMobileLayoutClass(desktopClass: string): string {
  if (desktopClass.includes("media-default-cropped")) {
    return desktopClass.replace("media-default-cropped", "media-full-width-cropped");
  }
  if (desktopClass.includes("media-default")) {
    return desktopClass.replace("media-default", "media-full-width");
  }
  return desktopClass;
}

/**
 * React NodeView for optimized article body images.
 */
function ImageNodeView({ node }: NodeViewProps) {
  const isMobile = useIsMobile();
  const { ref, inView } = useSmartImage();
  const src = node.attrs.src || "";
  const alt = node.attrs.alt || "";
  const title = node.attrs.title || undefined;

  // Get layout class, strip any stale rounded-3xl
  let layoutClass = (node.attrs.class || "media-default")
    .replace(/\s*rounded-3xl\s*/g, "")
    .trim() || "media-default";

  // Apply mobile layout translation
  if (isMobile) {
    layoutClass = getMobileLayoutClass(layoutClass);
  }

  const isFullWidth = layoutClass.includes("media-full-width");
  const radiusClass = isFullWidth ? "" : "rounded-3xl";
  const imgClassName = [layoutClass, radiusClass].filter(Boolean).join(" ");

  // Layout-aware optimization
  const config = getLayoutConfig(layoutClass);
  const optimizedSrc = optimizeImage(src, { width: config.mainWidth });
  const srcSet = optimizeImageSrcSet(src, config.srcSetWidths);

  return (
    <NodeViewWrapper ref={ref} as="div" className="image-node-wrapper">
      {inView && (
        <img
          src={optimizedSrc}
          srcSet={srcSet}
          sizes={srcSet ? config.sizes : undefined}
          alt={alt}
          title={title}
          className={imgClassName}
          loading="lazy"
          decoding="async"
        />
      )}
    </NodeViewWrapper>
  );
}

/**
 * Custom TipTap Image node with Cloudflare optimization via ReactNodeViewRenderer.
 * Replaces the standard @tiptap/extension-image in PostViewer.
 */
export const ImageNode = Node.create({
  name: "image",

  group: "block",

  atom: true,

  draggable: false,

  selectable: false,

  addAttributes() {
    return {
      src: {
        default: null,
        parseHTML: (element) => element.getAttribute("src"),
        renderHTML: (attributes) => ({ src: attributes.src }),
      },
      alt: {
        default: null,
        parseHTML: (element) => element.getAttribute("alt"),
        renderHTML: (attributes) => ({ alt: attributes.alt }),
      },
      title: {
        default: null,
        parseHTML: (element) => element.getAttribute("title"),
        renderHTML: (attributes) => ({ title: attributes.title }),
      },
      class: {
        default: "media-default",
        parseHTML: (element) => {
          const stored =
            element.getAttribute("data-class") ||
            element.getAttribute("class") ||
            "media-default";
          return stored.replace(/\s*rounded-3xl\s*/g, "").trim() || "media-default";
        },
        renderHTML: (attributes) => {
          return { "data-class": attributes.class };
        },
      },
    };
  },

  parseHTML() {
    return [{ tag: "img[src]" }];
  },

  renderHTML({ HTMLAttributes }) {
    return [
      "img",
      mergeAttributes(HTMLAttributes, {
        "data-class": HTMLAttributes.class,
      }),
    ];
  },

  addNodeView() {
    return ReactNodeViewRenderer(ImageNodeView);
  },
});

export default ImageNode;
